import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Plus,
  Trash,
  Receipt,
  Funnel,
  FileText,
  Warning,
  Eye,
  Printer,
  PencilSimple,
  EnvelopeSimple,
  GearSix,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { openPdfInBrowser, printPdf } from '../utils/pdfUtils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INVOICE_CATEGORIES = [
  { value: 'common_expenses', labelKey: 'commonExpenses' },
  { value: 'reserve_fund', labelKey: 'reserveFund' },
  { value: 'repair', labelKey: 'repair' },
];

// Categories that are apartment charges (billable to residents)
const APARTMENT_CHARGE_CATEGORIES = new Set([
  'management', 'common_expenses', 'reserve_fund', 'repair', 'extra_reserve',
]);

const EXPENSE_TYPES = [
  { value: 'monthly', labelKey: 'expTypeMonthly' },
  { value: 'capital', labelKey: 'expTypeCapital' },
];

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function InvoicesTab({ buildingId, apartments, openEmailDialog }) {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState(''); // 'single' or 'all'
  const [createForm, setCreateForm] = useState({
    apartment_id: '',
    total_amount: '',
    category: 'common_expenses',
    expense_type: 'monthly',
    period: new Date().toISOString().slice(0, 7),
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    description: '',
    notes: '',
    line_items: [{ description: '', period: new Date().toISOString().slice(0, 7), amount: '' }],
  });
  const [creating, setCreating] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', notes: '', period: '' });

  // Invoice number settings
  const [invNumSettings, setInvNumSettings] = useState(null);
  const [invNumSettingsOpen, setInvNumSettingsOpen] = useState(false);
  const [invNumForm, setInvNumForm] = useState({ prefix: 'INV', separator: '-', padding: 4, next_number: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [invNumDuplicate, setInvNumDuplicate] = useState(false);

  // Phase 1002r — Effective Reserve Fund preview. When the invoice
  // category is Common Expenses and the period has an active Extra
  // Monthly Reserve schedule, the backend will auto-append a second
  // line item to the invoice. We surface that here so the user sees
  // "€40 Common + €10 Reserve Fund = €50 total" BEFORE clicking Create.
  const [effectiveReserve, setEffectiveReserve] = useState(null);

  const fetchInvNumSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/invoice-number-settings`);
      setInvNumSettings(res.data);
      setInvNumForm({
        prefix: res.data.prefix || '',
        separator: res.data.separator === '' ? 'none' : (res.data.separator || '-'),
        padding: res.data.padding || 4,
        next_number: res.data.next_number || '',
      });
    } catch {}
  }, [buildingId]);

  const handleSaveInvNumSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = { ...invNumForm, separator: invNumForm.separator === 'none' ? '' : invNumForm.separator };
      await axios.put(`${API}/buildings/${buildingId}/invoice-number-settings`, payload);
      toast.success(t('settingsSaved'));
      await fetchInvNumSettings();
      setInvNumSettingsOpen(false);
      // Update the invoice number in the form to the new preview
      const newSep = invNumForm.separator === 'none' ? '' : invNumForm.separator;
      const newNum = invNumForm.next_number || (invNumSettings ? invNumSettings.next_number : 1);
      const newPreview = `${invNumForm.prefix}${newSep}${String(newNum).padStart(invNumForm.padding, '0')}`;
      setCreateForm(prev => ({ ...prev, invoice_number: newPreview }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSavingSettings(false);
    }
  };

  // Check for duplicate invoice number
  const checkDuplicate = useCallback(async (invNum) => {
    if (!invNum || !invNum.trim()) { setInvNumDuplicate(false); return; }
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/check-invoice-number?invoice_number=${encodeURIComponent(invNum)}`);
      setInvNumDuplicate(res.data.exists);
    } catch {
      setInvNumDuplicate(false);
    }
  }, [buildingId]);

  // Use server-side preview directly
  const _sep = invNumForm.separator === 'none' ? '' : (invNumForm.separator || '-');
  const _nextNum = invNumForm.next_number || (invNumSettings ? invNumSettings.next_number : 1);
  const invNumPreview = invNumSettings ? invNumSettings.preview : '';
  const invNumFormPreview = `${invNumForm.prefix}${_sep}${String(_nextNum).padStart(invNumForm.padding, '0')}`;

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/buildings/${buildingId}/apartment-invoices`, { params });
      setInvoices(res.data || []);
    } catch (err) {
      console.error('Failed to fetch invoices', err);
    } finally {
      setLoading(false);
    }
  }, [buildingId, statusFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchInvNumSettings(); }, [fetchInvNumSettings]);

  // Phase 1002r — Refresh Reserve Fund preview whenever the create form's
  // period, apartment_id, or category changes. Only fires while the
  // Create dialog is open and the category is a Common Expenses one.
  useEffect(() => {
    if (!createOpen) { setEffectiveReserve(null); return; }
    if (!['common_expenses', 'management'].includes(createForm.category)) {
      setEffectiveReserve(null);
      return;
    }
    if (!createForm.period) { setEffectiveReserve(null); return; }
    const params = new URLSearchParams({ period: createForm.period });
    if (createMode === 'single' && createForm.apartment_id) {
      params.set('apartment_id', createForm.apartment_id);
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/buildings/${buildingId}/extra-reserve-fund/effective?${params.toString()}`,
        );
        if (!cancelled) setEffectiveReserve(res.data);
      } catch { if (!cancelled) setEffectiveReserve(null); }
    })();
    return () => { cancelled = true; };
  }, [buildingId, createOpen, createMode, createForm.period, createForm.apartment_id, createForm.category]);

  const lineItemsTotal = createForm.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);

  const addLineItem = () => {
    setCreateForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', period: prev.period, amount: '' }],
    }));
  };

  const updateLineItem = (idx, field, value) => {
    setCreateForm(prev => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (idx) => {
    setCreateForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx),
    }));
  };

  const handleCreate = async () => {
    if (!createMode) return;
    setCreating(true);
    try {
      const payload = {
        mode: createMode,
        period: createForm.period,
        category: createForm.category,
        expense_type: createForm.expense_type,
        description: createForm.description,
        notes: createForm.notes,
        invoice_date: createForm.invoice_date,
        due_date: createForm.due_date,
      };
      if (createForm.invoice_number) {
        payload.invoice_number = createForm.invoice_number;
      }
      if (createMode === 'single') {
        payload.apartment_id = createForm.apartment_id;
        payload.line_items = createForm.line_items.filter(li => parseFloat(li.amount) > 0);
        payload.amount = lineItemsTotal;
      } else {
        payload.total_amount = parseFloat(createForm.total_amount) || 0;
      }
      const res = await axios.post(`${API}/buildings/${buildingId}/apartment-invoices/generate`, payload);
      toast.success(res.data.message);
      setCreateOpen(false);
      setCreateMode('');
      setCreateForm({
        apartment_id: '', total_amount: '',
        category: 'common_expenses', expense_type: 'monthly',
        period: new Date().toISOString().slice(0, 7),
        invoice_date: new Date().toISOString().slice(0, 10),
        due_date: '',
        description: '', notes: '',
        line_items: [{ description: '', period: new Date().toISOString().slice(0, 7), amount: '' }],
      });
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingInvoice) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/apartment-invoices/${deletingInvoice.invoice_id}`);
      toast.success(t('invoiceDeleted'));
      setDeleteOpen(false);
      setDeletingInvoice(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const openEdit = (inv) => {
    setEditingInvoice(inv);
    setEditForm({ amount: inv.amount || 0, notes: inv.notes || '', period: inv.period || '' });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editingInvoice) return;
    try {
      await axios.put(`${API}/apartments/${editingInvoice.apartment_id}/invoices/${editingInvoice.invoice_id}`, editForm);
      toast.success(t('invoiceUpdated'));
      setEditOpen(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  // Filter invoices — only show apartment charges (exclude building expenses)
  const filtered = invoices.filter(inv => {
    const cat = inv.category || inv.invoice_type || '';
    // Only show apartment-relevant categories
    if (!APARTMENT_CHARGE_CATEGORIES.has(cat)) return false;
    if (categoryFilter !== 'all' && cat !== categoryFilter && inv.invoice_type !== categoryFilter) return false;
    if (periodFilter && inv.period !== periodFilter) return false;
    return true;
  });

  // Summary
  const totalAmount = filtered.reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = filtered.reduce((s, i) => s + (i.paid_amount || 0), 0);
  const totalRemaining = filtered.reduce((s, i) => s + (i.remaining || 0), 0);

  // Unique periods for filter
  const periods = [...new Set(invoices.map(i => i.period).filter(Boolean))].sort().reverse();

  const getCatLabel = (cat) => {
    const map = {
      management: t('commonExpenses'), cleaning: t('catCleaning'),
      electricity: t('catElectricity'), insurance: t('catInsurance'),
      elevator: t('catElevator'), other: t('commonExpenses'),
      common_expenses: t('commonExpenses'), repair: t('repair'),
      reserve_fund: t('reserveFund'),
    };
    return map[cat] || cat || '-';
  };

  const getStatusBadge = (inv) => {
    // Prefer backend-derived payment_status (paid/partial/unpaid).
    // Fall back to legacy inv.status, normalizing partially_paid → partial.
    let s = inv.payment_status;
    if (!s) {
      const legacy = (inv.status || '').toLowerCase();
      if (legacy === 'partially_paid' || legacy === 'partial') s = 'partial';
      else if (legacy === 'paid') s = 'paid';
      else s = 'unpaid';
    }
    const map = {
      unpaid: { bg: 'bg-red-50 text-red-700 border-red-200', label: t('unpaid') },
      partial: { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: t('partial') },
      paid: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: t('paid') },
    };
    const info = map[s] || { bg: 'bg-zinc-50 text-zinc-700 border-zinc-200', label: s || '-' };
    return <span className={`text-xs px-2 py-0.5 border font-semibold uppercase ${info.bg}`} data-testid={`inv-status-${s}`}>{info.label}</span>;
  };

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-bold text-zinc-800 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Receipt size={20} weight="fill" className="text-blue-600" />
          {t('apartmentCharges')}
        </h3>
        {isAdmin && (
          <Button
            onClick={() => { setCreateMode(''); setCreateOpen(true); }}
            className="bg-zinc-950 rounded-none text-xs h-8 gap-1"
            data-testid="create-invoice-btn"
          >
            <Plus size={14} /> {t('createInvoice')}
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-zinc-200 p-3">
          <div className="text-xs text-zinc-500">{t('totalCharged')}</div>
          <div className="text-lg font-bold font-mono text-zinc-900">&euro;{totalAmount.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-3">
          <div className="text-xs text-zinc-500">{t('totalPaid')}</div>
          <div className="text-lg font-bold font-mono text-emerald-700">&euro;{totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-3">
          <div className="text-xs text-zinc-500">{t('totalRemaining')}</div>
          <div className="text-lg font-bold font-mono text-red-700">&euro;{totalRemaining.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-zinc-50 border border-zinc-200 p-2">
        <Funnel size={14} className="text-zinc-400" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-7 text-xs rounded-none border-zinc-300" data-testid="invoice-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">{t('allStatuses')}</SelectItem>
            <SelectItem value="unpaid">{t('unpaid')}</SelectItem>
            <SelectItem value="partial">{t('partial')}</SelectItem>
            <SelectItem value="paid">{t('paid')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[160px] h-7 text-xs rounded-none border-zinc-300" data-testid="invoice-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">{t('allCategories')}</SelectItem>
            {INVOICE_CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{t(c.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={periodFilter || '__all__'} onValueChange={v => setPeriodFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[130px] h-7 text-xs rounded-none border-zinc-300" data-testid="invoice-period-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">{t('allPeriods')}</SelectItem>
            {periods.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400">
          <FileText size={32} className="mx-auto mb-2 opacity-40" />
          <p>{t('noInvoices')}</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-xs" data-testid="invoices-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('invoiceNumber')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('apartment')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('owner')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('period')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('category')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('expenseTypeLabel')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600 text-right">{t('amount')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600 text-right">{t('paid')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600 text-right">{t('remaining')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('status')}</th>
                {isAdmin && <th className="px-3 py-2 font-semibold text-zinc-600 text-center">{t('actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="px-3 py-2 font-mono text-zinc-700">{inv.invoice_number || '-'}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{inv.apartment_number || '-'}</td>
                  <td className="px-3 py-2 text-zinc-600">{inv.owner_name || '-'}</td>
                  <td className="px-3 py-2 font-mono text-zinc-600">{inv.period || '-'}</td>
                  <td className="px-3 py-2">{getCatLabel(inv.category || inv.invoice_type)}</td>
                  <td className="px-3 py-2 text-zinc-500">{inv.expense_type === 'capital' ? t('expTypeCapital') : t('expTypeMonthly')}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">&euro;{(inv.amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">&euro;{(inv.paid_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">&euro;{(inv.remaining || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">{getStatusBadge(inv)}</td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-0.5">
                        <button onClick={() => openPdfInBrowser(`/apartments/${inv.apartment_id}/invoices/${inv.invoice_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('view')} data-testid={`view-invoice-${inv.invoice_id}`}><Eye size={14} /></button>
                        <button onClick={() => printPdf(`/apartments/${inv.apartment_id}/invoices/${inv.invoice_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('printPdf')} data-testid={`print-invoice-${inv.invoice_id}`}><Printer size={14} /></button>
                        {openEmailDialog && <button onClick={() => openEmailDialog(
                          `/api/apartments/${inv.apartment_id}/invoices/${inv.invoice_id}/pdf`,
                          `Invoice_${inv.invoice_number || inv.invoice_id}.pdf`,
                          `Invoice ${inv.invoice_number || ''}`,
                          inv.owner_email || ''
                        )} className="p-1 hover:bg-purple-50 text-zinc-400 hover:text-purple-600 transition-colors" title={t('sendByEmail')} data-testid={`email-invoice-${inv.invoice_id}`}><EnvelopeSimple size={14} /></button>}
                        <button onClick={() => openEdit(inv)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`edit-invoice-${inv.invoice_id}`}><PencilSimple size={14} /></button>
                        <button onClick={() => { setDeletingInvoice(inv); setDeleteOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-invoice-${inv.invoice_id}`}><Trash size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) setCreateMode(''); }}>
        <DialogContent className="sm:max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('createInvoice')}</DialogTitle>
            <DialogDescription className="sr-only">{t('selectInvoiceType')}</DialogDescription>
          </DialogHeader>

          {/* Step 1: Choose mode */}
          {!createMode && (
            <div className="space-y-3" data-testid="invoice-mode-select">
              <p className="text-sm text-zinc-600">{t('selectInvoiceType')}</p>
              <button
                onClick={() => {
                  setCreateMode('single');
                  // Pre-fill with auto-generated number
                  if (invNumPreview) {
                    setCreateForm(prev => ({ ...prev, invoice_number: invNumPreview }));
                  }
                }}
                className="w-full text-left border border-zinc-200 p-4 hover:bg-blue-50 hover:border-blue-300 transition-colors group"
                data-testid="invoice-mode-single"
              >
                <div className="font-semibold text-sm text-zinc-800 group-hover:text-blue-800">{t('singleApartment')}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{t('singleApartmentDesc')}</div>
              </button>
              <button
                onClick={() => setCreateMode('all')}
                className="w-full text-left border border-zinc-200 p-4 hover:bg-amber-50 hover:border-amber-300 transition-colors group"
                data-testid="invoice-mode-all"
              >
                <div className="font-semibold text-sm text-zinc-800 group-hover:text-amber-800">{t('allApartments')}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{t('allApartmentsDesc')}</div>
              </button>
            </div>
          )}

          {/* Step 2: Form */}
          {createMode && (
            <div className="space-y-4" data-testid="invoice-create-form">
              {/* Mode indicator */}
              <div className={`text-xs px-3 py-1.5 border ${createMode === 'single' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                {createMode === 'single' ? t('singleApartment') : t('allApartments')}
                <button onClick={() => setCreateMode('')} className="ml-2 underline text-xs opacity-70 hover:opacity-100">{t('change')}</button>
              </div>

              {/* Row 1: Invoice Number + Category */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center gap-1">
                    <Label className="text-xs text-zinc-500">{t('invoiceNumber')}</Label>
                    <Popover open={invNumSettingsOpen} onOpenChange={setInvNumSettingsOpen}>
                      <PopoverTrigger asChild>
                        <button type="button" className="p-0.5 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('invoiceNumberSettings')} data-testid="invoice-number-settings-btn">
                          <GearSix size={14} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 rounded-none p-0" align="start">
                        <div className="p-3 border-b border-zinc-200 bg-zinc-50">
                          <h4 className="text-xs font-bold text-zinc-700" style={{ fontFamily: 'var(--font-heading)' }}>{t('invoiceNumberSettings')}</h4>
                        </div>
                        <div className="p-3 space-y-3">
                          <div>
                            <Label className="text-[10px] text-zinc-500">{t('prefix')}</Label>
                            <Input value={invNumForm.prefix} onChange={e => setInvNumForm({ ...invNumForm, prefix: e.target.value.toUpperCase() })} className="rounded-none mt-0.5 h-7 text-xs font-mono" maxLength={6} data-testid="inv-settings-prefix" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-zinc-500">{t('separator')}</Label>
                              <Select value={invNumForm.separator} onValueChange={v => setInvNumForm({ ...invNumForm, separator: v })}>
                                <SelectTrigger className="rounded-none h-7 text-xs font-mono" data-testid="inv-settings-separator"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                  <SelectItem value="-">- (dash)</SelectItem>
                                  <SelectItem value="/">/  (slash)</SelectItem>
                                  <SelectItem value=".">. (dot)</SelectItem>
                                  <SelectItem value="none">  (none)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] text-zinc-500">{t('digits')}</Label>
                              <Select value={String(invNumForm.padding)} onValueChange={v => setInvNumForm({ ...invNumForm, padding: parseInt(v) })}>
                                <SelectTrigger className="rounded-none h-7 text-xs font-mono" data-testid="inv-settings-padding"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                  <SelectItem value="3">3</SelectItem>
                                  <SelectItem value="4">4</SelectItem>
                                  <SelectItem value="5">5</SelectItem>
                                  <SelectItem value="6">6</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] text-zinc-500">{t('nextNumber')}</Label>
                            <Input type="number" min={1} value={invNumForm.next_number} onChange={e => setInvNumForm({ ...invNumForm, next_number: parseInt(e.target.value) || '' })} className="rounded-none mt-0.5 h-7 text-xs font-mono" data-testid="inv-settings-next-number" />
                          </div>
                          {/* Live preview */}
                          <div className="bg-zinc-50 border border-zinc-200 p-2">
                            <div className="text-[10px] text-zinc-400 mb-0.5">{t('preview')}:</div>
                            <div className="font-mono text-sm font-bold text-zinc-800" data-testid="inv-settings-preview">
                              {invNumFormPreview}
                            </div>
                          </div>
                          <Button onClick={handleSaveInvNumSettings} disabled={savingSettings || !invNumForm.prefix} className="w-full bg-zinc-950 rounded-none text-xs h-7" data-testid="inv-settings-save-btn">
                            {savingSettings ? '...' : t('save')}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="relative mt-1">
                    <Input
                      value={createForm.invoice_number || ''}
                      onChange={e => {
                        setCreateForm({ ...createForm, invoice_number: e.target.value });
                        setInvNumDuplicate(false);
                      }}
                      onBlur={e => checkDuplicate(e.target.value)}
                      className={`rounded-none font-mono text-sm ${invNumDuplicate ? 'border-red-400 bg-red-50' : ''}`}
                      placeholder={invNumPreview || t('autoGenerated')}
                      data-testid="invoice-number-input"
                    />
                  </div>
                  {invNumDuplicate && (
                    <div className="text-[10px] text-red-600 mt-0.5 font-medium" data-testid="inv-num-duplicate-warning">{t('invoiceNumberExists')}</div>
                  )}
                  {!invNumDuplicate && invNumPreview && !createForm.invoice_number && (
                    <div className="text-[10px] text-zinc-400 mt-0.5 font-mono">{t('nextNumber')}: {invNumPreview}</div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('category')} *</Label>
                  <Select value={createForm.category} onValueChange={v => setCreateForm({ ...createForm, category: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="invoice-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {INVOICE_CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{t(c.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Period + Invoice Date + Due Date */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500">{t('period')} *</Label>
                  <Input
                    type="month"
                    value={createForm.period}
                    onChange={e => setCreateForm({ ...createForm, period: e.target.value })}
                    className="rounded-none mt-1 font-mono text-sm"
                    data-testid="invoice-period-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceDate')}</Label>
                  <Input
                    type="date"
                    value={createForm.invoice_date}
                    onChange={e => setCreateForm({ ...createForm, invoice_date: e.target.value })}
                    className="rounded-none mt-1 font-mono text-sm"
                    data-testid="invoice-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('dueDate')}</Label>
                  <Input
                    type="date"
                    value={createForm.due_date}
                    onChange={e => setCreateForm({ ...createForm, due_date: e.target.value })}
                    className="rounded-none mt-1 font-mono text-sm"
                    data-testid="invoice-due-date-input"
                  />
                </div>
              </div>

              {/* Single: Apartment select */}
              {createMode === 'single' && (
                <div>
                  <Label className="text-xs text-zinc-500">{t('apartment')} *</Label>
                  <Select value={createForm.apartment_id} onValueChange={v => setCreateForm({ ...createForm, apartment_id: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="invoice-apartment-select">
                      <SelectValue placeholder={t('selectApartment')} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {(apartments || []).filter(a => !a.is_archived).map(apt => (
                        <SelectItem key={apt.apartment_id} value={apt.apartment_id}>
                          {apt.apartment_number} — {apt.owner_name || '-'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Single: Line Items Table */}
              {createMode === 'single' && (
                <div className="border border-zinc-200" data-testid="line-items-section">
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-zinc-600">{t('lineItems')}</span>
                    <button onClick={addLineItem} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="add-line-item-btn">
                      <Plus size={12} /> {t('addLine')}
                    </button>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-100 bg-zinc-50/50">
                        <th className="px-3 py-1.5 text-left text-[10px] text-zinc-400 font-semibold w-[45%]">{t('description')}</th>
                        <th className="px-3 py-1.5 text-left text-[10px] text-zinc-400 font-semibold w-[25%]">{t('period')}</th>
                        <th className="px-3 py-1.5 text-right text-[10px] text-zinc-400 font-semibold w-[20%]">{t('amount')} (&euro;)</th>
                        <th className="w-[10%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {createForm.line_items.map((li, idx) => (
                        <tr key={idx} className="border-b border-zinc-100" data-testid={`line-item-${idx}`}>
                          <td className="px-2 py-1.5">
                            <Input
                              value={li.description}
                              onChange={e => updateLineItem(idx, 'description', e.target.value)}
                              className="rounded-none text-xs h-7 border-zinc-200"
                              placeholder={t('description')}
                              data-testid={`li-desc-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input
                              type="month"
                              value={li.period}
                              onChange={e => updateLineItem(idx, 'period', e.target.value)}
                              className="rounded-none text-xs h-7 font-mono border-zinc-200"
                              data-testid={`li-period-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={li.amount}
                              onChange={e => updateLineItem(idx, 'amount', e.target.value)}
                              className="rounded-none text-xs h-7 font-mono text-right border-zinc-200"
                              placeholder="0.00"
                              data-testid={`li-amount-${idx}`}
                            />
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            {createForm.line_items.length > 1 && (
                              <button onClick={() => removeLineItem(idx)} className="p-0.5 text-zinc-400 hover:text-red-500" data-testid={`li-remove-${idx}`}><Trash size={12} /></button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-50 border-t border-zinc-200">
                        <td colSpan={2} className="px-3 py-2 text-right text-xs font-semibold text-zinc-600">{t('total')}:</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-zinc-900" data-testid="line-items-total">&euro;{lineItemsTotal.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Phase 1002r — Reserve Fund auto-add preview. Shown for
                  Common Expenses invoices when the invoice period falls
                  inside an active Extra Monthly Reserve schedule. The
                  backend automatically appends this as a second line
                  item on the created invoice. */}
              {effectiveReserve && effectiveReserve.active && (createMode === 'single'
                ? (effectiveReserve.per_apartment_amount || 0) > 0
                : (effectiveReserve.monthly_total || 0) > 0 || (effectiveReserve.fixed_per_apartment || 0) > 0
              ) && (
                <div className="border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-start gap-3" data-testid="extra-reserve-preview">
                  <div className="mt-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">
                      {t('reserveFundAutoAdded') || 'Reserve Fund will be added automatically'}
                    </div>
                    <div className="text-xs text-emerald-900 mt-0.5">
                      {createMode === 'single' ? (
                        <>
                          + <span className="font-mono font-semibold">&euro;{(effectiveReserve.per_apartment_amount || 0).toFixed(2)}</span>{' '}
                          {t('reserveFund') || 'Reserve Fund'} → {t('newTotal') || 'new total'}:{' '}
                          <span className="font-mono font-semibold">&euro;{(lineItemsTotal + (effectiveReserve.per_apartment_amount || 0)).toFixed(2)}</span>
                        </>
                      ) : (
                        <>
                          {effectiveReserve.mode === 'fixed'
                            ? <>+ <span className="font-mono font-semibold">&euro;{(effectiveReserve.fixed_per_apartment || 0).toFixed(2)}</span> {t('perApartment') || 'per apartment'}</>
                            : <>+ {t('shareOf') || 'share of'} <span className="font-mono font-semibold">&euro;{(effectiveReserve.monthly_total || 0).toFixed(2)}</span> {t('monthlyTotal') || 'monthly total'}</>
                          }
                        </>
                      )}
                    </div>
                    <div className="text-[10px] text-emerald-700/80 mt-0.5">
                      {t('effectiveFrom') || 'Effective from'} {formatDateDMY(effectiveReserve.start_date)}
                      {effectiveReserve.end_date ? ` ${t('until') || 'until'} ${formatDateDMY(effectiveReserve.end_date)}` : ` (${t('openEnded') || 'open-ended'})`}
                    </div>
                  </div>
                </div>
              )}

              {/* All: Total amount + preview */}
              {createMode === 'all' && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('totalAmount')} (&euro;) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={createForm.total_amount}
                      onChange={e => setCreateForm({ ...createForm, total_amount: e.target.value })}
                      className="rounded-none mt-1 font-mono"
                      data-testid="invoice-total-amount-input"
                    />
                  </div>
                  {parseFloat(createForm.total_amount) > 0 && apartments?.length > 0 && (
                    <div className="bg-zinc-50 border border-zinc-200 p-3 max-h-48 overflow-y-auto">
                      <div className="text-xs font-semibold text-zinc-600 mb-2">{t('sharePreview')}</div>
                      {(() => {
                        const total = parseFloat(createForm.total_amount) || 0;
                        const activeApts = (apartments || []).filter(a => !a.is_archived && (a.share_percentage || 0) > 0);
                        const totalShare = activeApts.reduce((s, a) => s + (a.share_percentage || 0), 0);
                        return activeApts.map(apt => {
                          const share = apt.share_percentage || 0;
                          const amt = totalShare > 0 ? (total * share / totalShare) : 0;
                          return (
                            <div key={apt.apartment_id} className="flex items-center justify-between text-xs py-1 border-b border-zinc-100 last:border-0">
                              <span className="font-mono font-semibold">{apt.apartment_number}</span>
                              <span className="text-zinc-500">{share.toFixed(1)}%</span>
                              <span className="font-mono font-semibold">&euro;{amt.toFixed(2)}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* Notes */}
              <div>
                <Label className="text-xs text-zinc-500">{t('notes')}</Label>
                <Input
                  value={createForm.notes}
                  onChange={e => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="rounded-none mt-1"
                  placeholder={t('optionalDescription')}
                  data-testid="invoice-notes-input"
                />
              </div>

              <DialogFooter className="gap-2 pt-2">
                <Button variant="outline" onClick={() => setCreateMode('')} className="rounded-none">{t('back')}</Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || invNumDuplicate || (createMode === 'single' && (!createForm.apartment_id || lineItemsTotal <= 0)) || (createMode === 'all' && !createForm.total_amount)}
                  className="bg-zinc-950 rounded-none"
                  data-testid="invoice-submit-btn"
                >
                  {creating ? t('generating') : t('generateInvoices')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle>{t('deleteInvoice')}</DialogTitle>
            <DialogDescription className="sr-only">{t('confirmDeleteInvoice')}</DialogDescription>
          </DialogHeader>
          {deletingInvoice && (
            <div className="space-y-2">
              <p className="text-sm text-zinc-600">{t('confirmDeleteInvoice')}</p>
              <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs font-mono space-y-1">
                <div>{t('invoiceNumber')}: {deletingInvoice.invoice_number || '-'}</div>
                <div>{t('apartment')}: {deletingInvoice.apartment_number}</div>
                <div>{t('amount')}: &euro;{(deletingInvoice.amount || 0).toFixed(2)}</div>
              </div>
              {(deletingInvoice.paid_amount || 0) > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                  <Warning size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{t('invoiceHasPaymentsWarning')}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-none">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-invoice-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Invoice Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle>{t('editInvoice')}</DialogTitle>
            <DialogDescription className="sr-only">{t('editInvoice')}</DialogDescription>
          </DialogHeader>
          {editingInvoice && (
            <div className="space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 p-2 text-xs font-mono">
                {editingInvoice.invoice_number || '-'} — {editingInvoice.apartment_number || '-'}
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })} className="rounded-none mt-1 font-mono" data-testid="edit-inv-amount" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('period')}</Label>
                <Input type="month" value={editForm.period} onChange={e => setEditForm({ ...editForm, period: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="edit-inv-period" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('notes')}</Label>
                <Input value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="edit-inv-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">{t('cancel')}</Button>
            <Button onClick={handleEdit} className="bg-zinc-950 rounded-none" data-testid="confirm-edit-invoice-btn">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
